import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

/* ─── constants ─────────────────────────────────────────── */
const F   = "'Poppins','Nunito',sans-serif";
const IST = () => new Date(new Date().getTime() + (330 - new Date().getTimezoneOffset()) * 60000);
const toDateStr = d => d.toISOString().split('T')[0];
const todayIST  = ()  => toDateStr(IST());

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const SC = { scheduled:'#F59E0B', posted:'#22C55E', skipped:'#94A3B8', empty:'#E2E8F0' };
const SL = { scheduled:'Scheduled', posted:'Posted', skipped:'Skipped', empty:'Empty' };

const mkCapEn = (v, r) =>
`Good morning! 🙏 Today's Word of God\n\n"${v}"\n— ${r}\n\nZion Assemblies of God Church, Ooty\n#ZionChurch #ZionOoty #DailyVerse #Bible #WordOfGod #AssembliesOfGod`;

const mkCapTa = (v, r) =>
`🕊️ இன்றைய வேத வாக்கியம்\n\n"${v}"\n— ${r}\n\nZion Assemblies of God Church, Ooty\n#ZionChurch #DailyVerse #Bible`;

/* ─── POST MODES ─────────────────────────────────────────── */
const POST_MODES = [
  { key:'combined', label:'Combined',     icon:'🔗', desc:'EN + TA together in one post' },
  { key:'en_only',  label:'English Only', icon:'🇬🇧', desc:'Post only English content'    },
  { key:'ta_only',  label:'Tamil Only',   icon:'🕊️', desc:'Post only Tamil content'      },
  { key:'separate', label:'Separate',     icon:'↔️', desc:'EN post first, then TA post'  },
];

/* ─── platform config ────────────────────────────────────── */
const PLATFORMS = [
  { key:'facebook',  label:'Facebook',         bg:'#1877F2', c:'#fff' },
  { key:'instagram', label:'Instagram',        bg:'linear-gradient(135deg,#F09433,#DC2743,#BC1888)', c:'#fff' },
  { key:'whatsapp',  label:'WhatsApp',         bg:'#25D366', c:'#fff' },
  { key:'website',   label:'Website + Daily Verse', bg:'#1E293B', c:'#A3E635' },
];

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function VersePostTab() {
  /* view */
  const [view,        setView]        = useState('calendar');
  const [calYear,     setCalYear]     = useState(IST().getFullYear());
  const [calMonth,    setCalMonth]    = useState(IST().getMonth());
  const [scheduleMap, setScheduleMap] = useState({});
  const [selectedDate,setSelectedDate]= useState(todayIST());

  /* post settings */
  const [postMode,     setPostMode]     = useState('combined');
  const [scheduleTime, setScheduleTime] = useState('00:00');

  /* images */
  const [imgEnFile, setImgEnFile] = useState(null);
  const [imgTaFile, setImgTaFile] = useState(null);
  const [previewEn, setPreviewEn] = useState('');
  const [previewTa, setPreviewTa] = useState('');
  const [urlEn,     setUrlEn]     = useState('');
  const [urlTa,     setUrlTa]     = useState('');

  /* verse text */
  const [verseEn,   setVerseEn]   = useState('');
  const [refEn,     setRefEn]     = useState('');
  const [verseTa,   setVerseTa]   = useState('');
  const [refTa,     setRefTa]     = useState('');
  const [caption,   setCaption]   = useState('');
  const [captionTa, setCaptionTa] = useState('');

  /* state */
  const [aiLoading,  setAiLoading]  = useState({ en:false, ta:false });
  const [uploading,  setUploading]  = useState({ en:false, ta:false });
  const [posting,    setPosting]    = useState(false);
  const [postStatus, setPostStatus] = useState({});
  const [msg,        setMsg]        = useState('');
  const [msgType,    setMsgType]    = useState('success');
  const [whatsappNo, setWhatsappNo] = useState('917339326116');
  const [syncedToday,setSyncedToday]= useState(false);

  const fileEnRef = useRef();
  const fileTaRef = useRef();

  const flash = (text, type='success') => { setMsg(text); setMsgType(type); setTimeout(()=>setMsg(''),5000); };

  /* load WhatsApp number from church_settings */
  useEffect(() => {
    supabase.from('church_settings').select('key,value')
      .then(({ data }) => {
        if (data) {
          const m = {}; data.forEach(r => { m[r.key] = r.value; });
          if (m.church_phone) setWhatsappNo(m.church_phone.replace(/[^0-9]/g,''));
        }
      });
  }, []);

  /* load schedule month */
  const loadSchedule = useCallback(async () => {
    const y = calYear, mo = calMonth;
    const first = `${y}-${String(mo+1).padStart(2,'0')}-01`;
    const last  = `${y}-${String(mo+1).padStart(2,'0')}-${new Date(y,mo+1,0).getDate()}`;
    const { data } = await supabase.from('verse_schedule').select('*').gte('scheduled_date',first).lte('scheduled_date',last);
    const map = {};
    if (data) data.forEach(r => { map[r.scheduled_date] = r; });
    setScheduleMap(map);
  }, [calYear, calMonth]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  /* open a date in editor */
  const openDate = async (dateStr) => {
    setSelectedDate(dateStr);
    setPostStatus({});
    setSyncedToday(false);
    const ex = scheduleMap[dateStr];
    if (ex) {
      setPreviewEn(ex.image_url_en || '');  setUrlEn(ex.image_url_en || '');
      setPreviewTa(ex.image_url_ta || '');  setUrlTa(ex.image_url_ta || '');
      setVerseEn(ex.verse_text_en  || '');  setRefEn(ex.verse_ref_en   || '');
      setVerseTa(ex.verse_text_ta  || '');  setRefTa(ex.verse_ref_ta   || '');
      setCaption(ex.caption        || '');
      setCaptionTa(ex.caption_ta   || '');
      setPostMode(ex.post_mode     || 'combined');
      setScheduleTime(ex.schedule_time || '00:00');
    } else {
      setPreviewEn(''); setUrlEn(''); setImgEnFile(null);
      setPreviewTa(''); setUrlTa(''); setImgTaFile(null);
      setVerseEn(''); setRefEn(''); setVerseTa(''); setRefTa('');
      setCaption(''); setCaptionTa('');
      setPostMode('combined'); setScheduleTime('00:00');
    }
    setView('editor');
  };

  /* file pick */
  const handleFile = (lang, e) => {
    const f = e.target.files[0]; if (!f) return;
    if (lang==='en') { setImgEnFile(f); setPreviewEn(URL.createObjectURL(f)); setUrlEn(''); }
    else             { setImgTaFile(f); setPreviewTa(URL.createObjectURL(f)); setUrlTa(''); }
    e.target.value = '';
  };

  /* upload one image */
  const uploadOne = async (lang) => {
    const file = lang==='en' ? imgEnFile : imgTaFile;
    if (!file) return lang==='en' ? urlEn : urlTa;
    setUploading(u=>({...u,[lang]:true}));
    const fn = `verse/${selectedDate}_${lang}_${Date.now()}.${file.name.split('.').pop()}`;
    const { error } = await supabase.storage.from('gallery').upload(fn, file, { upsert:true, contentType:file.type });
    setUploading(u=>({...u,[lang]:false}));
    if (error) { flash('Upload failed: '+error.message,'error'); return null; }
    const url = supabase.storage.from('gallery').getPublicUrl(fn).data.publicUrl;
    if (lang==='en') setUrlEn(url); else setUrlTa(url);
    return url;
  };

  /* AI read */
  const aiReadImage = async (lang) => {
    const url = lang==='en' ? (urlEn || (imgEnFile ? await uploadOne('en'):null)) : (urlTa || (imgTaFile ? await uploadOne('ta'):null));
    if (!url) { flash(`Upload ${lang==='en'?'English':'Tamil'} image first`,'error'); return; }
    setAiLoading(a=>({...a,[lang]:true}));
    try {
      const r = await fetch('/api/read-verse-image', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({imageUrl:url,lang}) });
      const d = await r.json();
      if (d.success) {
        if (lang==='en') { setVerseEn(d.verse_text||''); setRefEn(d.verse_ref||''); setCaption(mkCapEn(d.verse_text||'',d.verse_ref||'')); flash('✅ AI read English image!'); }
        else             { setVerseTa(d.verse_text||''); setRefTa(d.verse_ref||''); setCaptionTa(mkCapTa(d.verse_text||'',d.verse_ref||'')); flash('✅ AI read Tamil image!'); }
      } else flash('AI failed: '+d.error,'error');
    } catch(e) { flash('Error: '+e.message,'error'); }
    setAiLoading(a=>({...a,[lang]:false}));
  };

  /* sync to daily_verses + verse_schedule */
  const syncDailyVerse = async (enUrl, taUrl) => {
    try {
      await supabase.from('daily_verses').upsert({
        scheduled_date: selectedDate,
        english_text:   verseEn, english_ref: refEn,
        tamil_text:     verseTa||null, tamil_ref: refTa||null,
      }, { onConflict:'scheduled_date' });
      setSyncedToday(true);
    } catch {}
  };

  /* SAVE to schedule */
  const saveSchedule = async () => {
    let en = urlEn; if (imgEnFile && !en) { en = await uploadOne('en'); if (!en) return; }
    let ta = urlTa; if (imgTaFile && !ta) { ta = await uploadOne('ta'); }

    const row = {
      scheduled_date: selectedDate,
      image_url_en:   en   || null,
      image_url_ta:   ta   || null,
      verse_text_en:  verseEn,
      verse_ref_en:   refEn,
      verse_text_ta:  verseTa,
      verse_ref_ta:   refTa,
      caption,
      caption_ta:     captionTa || null,
      post_mode:      postMode,
      schedule_time:  scheduleTime,
      status:         'scheduled',
    };

    const ex = scheduleMap[selectedDate];
    if (ex) {
      await supabase.from('verse_schedule').update(row).eq('id', ex.id);
    } else {
      await supabase.from('verse_schedule').insert([row]);
    }

    // Auto-sync verse text to daily_verses table
    await syncDailyVerse(en, ta);

    flash(`✅ Saved & synced for ${selectedDate} at ${scheduleTime} IST!`);
    await loadSchedule();
  };

  /* POST NOW single platform */
  const postNow = async (platform) => {
    let en = urlEn; if (imgEnFile && !en) { en = await uploadOne('en'); if (!en) return; }
    let ta = urlTa; if (imgTaFile && !ta) { ta = await uploadOne('ta'); }

    const needsEn = postMode !== 'ta_only';
    const needsTa = postMode !== 'en_only';

    if (needsEn && !en) { flash('Upload English image first','error'); return; }
    if (postMode==='ta_only' && !ta) { flash('Upload Tamil image first','error'); return; }

    setPostStatus(p=>({...p,[platform]:'loading'}));

    if (platform === 'website') {
      try {
        await supabase.from('verse_popup').update({ active:false }).eq('active',true);
        const { error } = await supabase.from('verse_popup').insert([{
          image_url: needsEn ? en : ta,
          image_url_tamil: (needsTa && ta) ? ta : null,
          caption, verse_text:verseEn, verse_ref:refEn,
          verse_text_tamil: verseTa||null, verse_ref_tamil:refTa||null, active:true,
        }]);
        await syncDailyVerse(en, ta);
        const ex = scheduleMap[selectedDate];
        if (ex) await supabase.from('verse_schedule').update({ status:'posted', popup_active:true, posted_at:new Date().toISOString() }).eq('id',ex.id);
        setPostStatus(p=>({...p,website: error?'error':'success'}));
        if (!error) { flash('✅ Website popup active & Daily Verse synced!'); loadSchedule(); }
      } catch(e) { setPostStatus(p=>({...p,website:'error'})); flash(e.message,'error'); }
      return;
    }

    if (platform === 'whatsapp') {
      try {
        // Build WhatsApp payload based on post mode
        const payload = {
          platform:'whatsapp', whatsappNumber: whatsappNo,
          imageUrl:     (postMode==='ta_only') ? ta : en,
          imageUrlTamil:(postMode==='separate' || postMode==='combined') ? ta||null : null,
          caption:      (postMode==='ta_only') ? captionTa : caption,
          captionTa:    (postMode==='separate' || postMode==='combined') ? captionTa||null : null,
          postMode,
          verseTextTa:  verseTa||null, verseRefTa: refTa||null,
        };
        const r = await fetch('/api/post-verse', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload) });
        const d = await r.json();
        setPostStatus(p=>({...p,whatsapp: d.success?'success':'error'}));
        if (d.success) flash('✅ Sent to WhatsApp!');
        else flash('WhatsApp: '+d.error,'error');
      } catch(e) { setPostStatus(p=>({...p,whatsapp:'error'})); flash(e.message,'error'); }
      return;
    }

    /* facebook / instagram */
    try {
      const payload = {
        platform,
        imageUrl:      (postMode==='ta_only') ? ta : en,
        imageUrlTamil: (postMode==='separate'||postMode==='combined') ? ta||null : null,
        caption:       (postMode==='ta_only') ? captionTa : caption,
        postMode,
      };
      const r = await fetch('/api/post-verse', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload) });
      const d = await r.json();
      setPostStatus(p=>({...p,[platform]: d.success?'success':'error'}));
      if (d.success) flash(`✅ Posted to ${platform}!`);
      else flash(`${platform}: `+d.error,'error');
    } catch(e) { setPostStatus(p=>({...p,[platform]:'error'})); flash(e.message,'error'); }
  };

  /* POST ALL */
  const postAll = async () => {
    setPosting(true);
    let en = urlEn; if (imgEnFile && !en) { en = await uploadOne('en'); if (!en) { setPosting(false); return; } }
    let ta = urlTa; if (imgTaFile && !ta) { ta = await uploadOne('ta'); }
    if (postMode !== 'ta_only' && !en) { flash('Upload English image first','error'); setPosting(false); return; }
    if (postMode === 'ta_only'  && !ta) { flash('Upload Tamil image first','error');   setPosting(false); return; }

    await saveSchedule();

    const results = {};

    // Website
    try {
      await supabase.from('verse_popup').update({ active:false }).eq('active',true);
      const { error } = await supabase.from('verse_popup').insert([{ image_url: postMode==='ta_only'?ta:en, image_url_tamil:ta||null, caption, verse_text:verseEn, verse_ref:refEn, verse_text_tamil:verseTa||null, verse_ref_tamil:refTa||null, active:true }]);
      await syncDailyVerse(en, ta);
      results.website = !error;
      setPostStatus(p=>({...p,website: !error?'success':'error'}));
    } catch { results.website=false; setPostStatus(p=>({...p,website:'error'})); }

    // Facebook
    setPostStatus(p=>({...p,facebook:'loading'}));
    try {
      const d = await fetch('/api/post-verse',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({platform:'facebook',imageUrl:postMode==='ta_only'?ta:en,imageUrlTamil:ta||null,caption,postMode})}).then(r=>r.json());
      results.facebook = d.success;
      setPostStatus(p=>({...p,facebook:d.success?'success':'error'}));
    } catch { results.facebook=false; setPostStatus(p=>({...p,facebook:'error'})); }

    await new Promise(r=>setTimeout(r,3000));

    // Instagram
    setPostStatus(p=>({...p,instagram:'loading'}));
    try {
      const d = await fetch('/api/post-verse',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({platform:'instagram',imageUrl:postMode==='ta_only'?ta:en,imageUrlTamil:ta||null,caption,postMode})}).then(r=>r.json());
      results.instagram = d.success;
      setPostStatus(p=>({...p,instagram:d.success?'success':'error'}));
    } catch { results.instagram=false; setPostStatus(p=>({...p,instagram:'error'})); }

    await new Promise(r=>setTimeout(r,2000));

    // WhatsApp (uses updated number from church_settings)
    setPostStatus(p=>({...p,whatsapp:'loading'}));
    try {
      const d = await fetch('/api/post-verse',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({platform:'whatsapp',whatsappNumber:whatsappNo,imageUrl:postMode==='ta_only'?ta:en,imageUrlTamil:ta||null,caption,captionTa:captionTa||null,postMode,verseTextTa:verseTa||null,verseRefTa:refTa||null})}).then(r=>r.json());
      results.whatsapp = d.success;
      setPostStatus(p=>({...p,whatsapp:d.success?'success':'error'}));
    } catch { results.whatsapp=false; setPostStatus(p=>({...p,whatsapp:'error'})); }

    // Admin notify
    try {
      const fmt = ok => ok?'✅ Success':'❌ Failed';
      const allOk = Object.values(results).every(Boolean);
      await fetch('/api/post-verse',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({platform:'notify',message:`Hi Admins! 🙏✝️\n\nZion Church — Daily Verse Post Report\n📅 Date: ${selectedDate}\n📋 Mode: ${postMode}\n\n📊 Status:\n• Facebook   → ${fmt(results.facebook)}\n• Instagram  → ${fmt(results.instagram)}\n• WhatsApp   → ${fmt(results.whatsapp)}\n• Website    → ${fmt(results.website)}\n\n${allOk?'🎉 All platforms posted successfully!':'⚠️ Some platforms failed. Check admin panel.'}\n\n— Zion Church Admin System`})});
    } catch {}

    setPosting(false);
  };

  /* delete schedule */
  const deleteSchedule = async (dateStr) => {
    if (!window.confirm(`Delete schedule for ${dateStr}?`)) return;
    const row = scheduleMap[dateStr]; if (!row) return;
    for (const u of [row.image_url_en, row.image_url_ta].filter(Boolean)) {
      try { const p = u.split('/gallery/')[1]; if(p) await supabase.storage.from('gallery').remove([p]); } catch {}
    }
    await supabase.from('verse_schedule').delete().eq('id',row.id);
    flash(`Deleted schedule for ${dateStr}`);
    loadSchedule(); setView('calendar');
  };

  /* calendar helpers */
  const daysInMonth    = new Date(calYear, calMonth+1, 0).getDate();
  const firstDayOfWeek = new Date(calYear, calMonth,   1).getDay();
  const si = k => ({loading:'⏳',success:'✅',error:'❌'}[postStatus[k]]||'');

  const needsEn = postMode !== 'ta_only';
  const needsTa = postMode !== 'en_only';

  /* ── styles ── */
  const card = { background:'#fff', border:'1px solid #F1F5F9', borderRadius:14, padding:'20px', marginBottom:14, boxShadow:'0 2px 10px rgba(0,0,0,0.05)' };
  const inp  = { width:'100%', padding:'10px 14px', background:'#F8FAFC', border:'1.5px solid #E2E8F0', borderRadius:9, color:'#1E293B', fontFamily:F, fontSize:'0.86rem', outline:'none', boxSizing:'border-box' };
  const lbl  = { display:'block', fontFamily:F, fontSize:'0.63rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#64748B', marginBottom:5, fontWeight:700 };
  const btn  = (bg, c='#fff', extra={}) => ({ background:bg, border:'none', color:c, fontFamily:F, fontWeight:700, fontSize:'0.78rem', padding:'10px 16px', borderRadius:10, cursor:'pointer', display:'flex', alignItems:'center', gap:6, justifyContent:'center', transition:'opacity 0.15s', ...extra });

  return (
    <div style={{ fontFamily:F, minHeight:'100vh', background:'#F8FAFC' }}>

      {/* ── Flash message ── */}
      {msg && (
        <div style={{ background: msgType==='error'?'#FEF2F2':'#F0FDF4', border:`1.5px solid ${msgType==='error'?'#FCA5A5':'#86EFAC'}`, borderRadius:12, padding:'12px 18px', marginBottom:16, fontSize:'0.84rem', fontFamily:F, fontWeight:600, color: msgType==='error'?'#DC2626':'#16A34A', display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:'1.1rem' }}>{msgType==='error'?'⚠️':'✅'}</span> {msg}
        </div>
      )}

      {/* ── View toggle ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:18 }}>
        <button onClick={()=>setView('calendar')} style={{ ...btn(view==='calendar'?'#1E293B':'#fff', view==='calendar'?'#A3E635':'#64748B'), border: view==='calendar'?'none':'1.5px solid #E2E8F0', padding:'12px', fontSize:'0.82rem', boxShadow: view==='calendar'?'0 4px 14px rgba(30,41,59,0.25)':'none' }}>
          📅 Schedule Calendar
        </button>
        <button onClick={()=>{ setSelectedDate(todayIST()); openDate(todayIST()); }} style={{ ...btn('linear-gradient(135deg,#F59E0B,#D97706)'), padding:'12px', fontSize:'0.82rem', boxShadow:'0 4px 14px rgba(245,158,11,0.35)' }}>
          ✏️ Edit Today ({todayIST()})
        </button>
      </div>

      {/* ══════════════════════════════════════════
          CALENDAR VIEW
      ══════════════════════════════════════════ */}
      {view === 'calendar' && (
        <div style={card}>
          {/* month nav */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <button onClick={()=>{ if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); }} style={{ ...btn('#F8FAFC','#1E293B'), border:'1.5px solid #E2E8F0', padding:'8px 14px', fontSize:'0.9rem' }}>←</button>
            <div style={{ fontFamily:F, fontWeight:800, fontSize:'1rem', color:'#1E293B' }}>{MONTHS[calMonth]} {calYear}</div>
            <button onClick={()=>{ if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); }} style={{ ...btn('#F8FAFC','#1E293B'), border:'1.5px solid #E2E8F0', padding:'8px 14px', fontSize:'0.9rem' }}>→</button>
          </div>

          {/* day headers */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6, marginBottom:6 }}>
            {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontFamily:F, fontSize:'0.62rem', fontWeight:700, color:'#94A3B8', padding:'4px 0' }}>{d}</div>)}
          </div>

          {/* days grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6 }}>
            {Array.from({length:firstDayOfWeek}).map((_,i)=><div key={`e${i}`}/>)}
            {Array.from({length:daysInMonth}).map((_,i)=>{
              const day = i+1;
              const ds  = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const row = scheduleMap[ds];
              const status = row ? row.status : 'empty';
              const isToday = ds === todayIST();
              const isPast  = ds < todayIST();
              return (
                <div key={day} onClick={()=>openDate(ds)} style={{
                  aspectRatio:'1', borderRadius:10, cursor:'pointer',
                  border:`2px solid ${isToday?'#84CC16':status!=='empty'?SC[status]:'#E2E8F0'}`,
                  background: isToday?'#F0FDF4' : status!=='empty'?`${SC[status]}18` : isPast?'#F8FAFC':'#fff',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  position:'relative', overflow:'hidden', transition:'all 0.15s',
                  opacity: isPast && status==='empty' ? 0.45 : 1,
                }}>
                  {row?.image_url_en && (
                    <div style={{ position:'absolute', inset:0, opacity:0.12 }}>
                      <img src={row.image_url_en} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                    </div>
                  )}
                  <div style={{ fontFamily:F, fontSize:'0.78rem', fontWeight: isToday?800:500, color: isToday?'#16A34A':'#1E293B', position:'relative', zIndex:1 }}>{day}</div>
                  {status!=='empty' && <div style={{ fontSize:'0.55rem', color:SC[status], fontWeight:700, position:'relative', zIndex:1 }}>{status==='posted'?'✓':status==='scheduled'?'●':''}</div>}
                </div>
              );
            })}
          </div>

          {/* legend */}
          <div style={{ display:'flex', gap:16, marginTop:16, flexWrap:'wrap' }}>
            {Object.entries(SL).map(([k,v])=>(
              <div key={k} style={{ display:'flex', alignItems:'center', gap:6, fontFamily:F, fontSize:'0.68rem', color:'#64748B' }}>
                <div style={{ width:10, height:10, borderRadius:3, background:SC[k] }}/>{v}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          EDITOR VIEW
      ══════════════════════════════════════════ */}
      {view === 'editor' && (
        <div>
          {/* Date header */}
          <div style={{ ...card, background: selectedDate===todayIST()?'#F0FDF4':'#F8FAFC', borderColor: selectedDate===todayIST()?'#86EFAC':'#E2E8F0', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
              <div>
                <div style={{ fontFamily:F, fontWeight:800, fontSize:'1rem', color:'#1E293B' }}>
                  📅 {selectedDate} {selectedDate===todayIST()&&<span style={{ background:'#84CC16', color:'#fff', fontSize:'0.6rem', fontWeight:700, padding:'2px 8px', borderRadius:20, marginLeft:6 }}>TODAY</span>}
                </div>
                <div style={{ fontFamily:F, fontSize:'0.72rem', color:'#64748B', marginTop:4 }}>
                  Status: <strong style={{ color:SC[scheduleMap[selectedDate]?.status||'empty'] }}>{SL[scheduleMap[selectedDate]?.status||'empty']}</strong>
                  {syncedToday && <span style={{ background:'#F0FDF4', color:'#16A34A', fontSize:'0.62rem', fontWeight:700, padding:'2px 8px', borderRadius:20, marginLeft:10 }}>✓ Synced to Daily Verse</span>}
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={()=>setView('calendar')} style={{ ...btn('#F8FAFC','#64748B'), border:'1.5px solid #E2E8F0', fontSize:'0.72rem', padding:'7px 12px' }}>← Calendar</button>
                {scheduleMap[selectedDate] && (
                  <button onClick={()=>deleteSchedule(selectedDate)} style={{ ...btn('#FEF2F2','#DC2626'), border:'1.5px solid #FCA5A5', fontSize:'0.72rem', padding:'7px 12px' }}>🗑 Delete</button>
                )}
              </div>
            </div>
          </div>

          {/* ── POST MODE SELECTOR ── */}
          <div style={{ ...card, padding:'18px 20px' }}>
            <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.82rem', color:'#1E293B', marginBottom:12 }}>📋 Post Mode — How to publish this verse?</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
              {POST_MODES.map(m => (
                <button key={m.key} onClick={()=>setPostMode(m.key)} style={{
                  background: postMode===m.key ? '#1E293B' : '#F8FAFC',
                  border: `2px solid ${postMode===m.key?'#84CC16':'#E2E8F0'}`,
                  borderRadius:12, padding:'12px 10px', cursor:'pointer', transition:'all 0.15s',
                  boxShadow: postMode===m.key?'0 4px 14px rgba(30,41,59,0.2)':'none',
                }}>
                  <div style={{ fontSize:'1.3rem', marginBottom:5 }}>{m.icon}</div>
                  <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.73rem', color: postMode===m.key?'#A3E635':'#1E293B', marginBottom:3 }}>{m.label}</div>
                  <div style={{ fontFamily:F, fontSize:'0.6rem', color: postMode===m.key?'rgba(255,255,255,0.5)':'#94A3B8', lineHeight:1.3 }}>{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>

            {/* ════ LEFT ════ */}
            <div>
              {/* Image upload */}
              <div style={card}>
                <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.85rem', color:'#1E293B', marginBottom:14 }}>📸 Verse Images</div>
                <div style={{ display:'grid', gridTemplateColumns: needsEn && needsTa ? '1fr 1fr' : '1fr', gap:12 }}>
                  {needsEn && (
                    <ImageSlot lang="en" label="🇬🇧 English" preview={previewEn} fileRef={fileEnRef}
                      uploading={uploading.en} aiLoading={aiLoading.en}
                      onFile={e=>handleFile('en',e)} onAI={()=>aiReadImage('en')} required />
                  )}
                  {needsTa && (
                    <ImageSlot lang="ta" label="🕊️ Tamil" preview={previewTa} fileRef={fileTaRef}
                      uploading={uploading.ta} aiLoading={aiLoading.ta}
                      onFile={e=>handleFile('ta',e)} onAI={()=>aiReadImage('ta')} required={postMode==='ta_only'} />
                  )}
                </div>
              </div>

              {/* Verse text */}
              <div style={card}>
                <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.85rem', color:'#1E293B', marginBottom:14 }}>📖 Verse Details</div>
                <div style={{ display:'grid', gridTemplateColumns: needsEn && needsTa ? '1fr 1fr' : '1fr', gap:12 }}>
                  {needsEn && (
                    <div>
                      <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#EFF6FF', color:'#1D4ED8', borderRadius:20, padding:'3px 10px', fontSize:'0.65rem', fontWeight:700, marginBottom:8 }}>🇬🇧 English</div>
                      <label style={lbl}>Verse Text</label>
                      <textarea value={verseEn} onChange={e=>{setVerseEn(e.target.value);setCaption(mkCapEn(e.target.value,refEn));}}
                        style={{ ...inp, height:76, resize:'vertical', marginBottom:8 }}/>
                      <label style={lbl}>Reference</label>
                      <input value={refEn} onChange={e=>{setRefEn(e.target.value);setCaption(mkCapEn(verseEn,e.target.value));}} style={inp} placeholder="e.g. John 3:16"/>
                    </div>
                  )}
                  {needsTa && (
                    <div>
                      <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#FFF7ED', color:'#C2410C', borderRadius:20, padding:'3px 10px', fontSize:'0.65rem', fontWeight:700, marginBottom:8 }}>🕊️ Tamil</div>
                      <label style={lbl}>Verse Text</label>
                      <textarea value={verseTa} onChange={e=>{setVerseTa(e.target.value);setCaptionTa(mkCapTa(e.target.value,refTa));}}
                        style={{ ...inp, height:76, resize:'vertical', marginBottom:8 }}/>
                      <label style={lbl}>Reference</label>
                      <input value={refTa} onChange={e=>{setRefTa(e.target.value);setCaptionTa(mkCapTa(verseTa,e.target.value));}} style={inp}/>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ════ RIGHT ════ */}
            <div>
              {/* Caption */}
              <div style={card}>
                <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.85rem', color:'#1E293B', marginBottom:12 }}>📝 Captions</div>
                {needsEn && (
                  <>
                    <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#EFF6FF', color:'#1D4ED8', borderRadius:20, padding:'3px 10px', fontSize:'0.65rem', fontWeight:700, marginBottom:6 }}>🇬🇧 English Caption</div>
                    <textarea value={caption} onChange={e=>setCaption(e.target.value)}
                      style={{ ...inp, height:100, resize:'vertical', marginBottom:12, lineHeight:1.6, fontSize:'0.78rem' }}/>
                  </>
                )}
                {needsTa && (
                  <>
                    <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#FFF7ED', color:'#C2410C', borderRadius:20, padding:'3px 10px', fontSize:'0.65rem', fontWeight:700, marginBottom:6 }}>🕊️ Tamil Caption {postMode!=='ta_only'&&<span style={{opacity:0.6,fontWeight:400}}>(optional)</span>}</div>
                    <textarea value={captionTa} onChange={e=>setCaptionTa(e.target.value)}
                      style={{ ...inp, height:88, resize:'vertical', marginBottom:0, lineHeight:1.6, fontSize:'0.78rem' }}
                      placeholder="Tamil caption auto-fills when you enter Tamil verse…"/>
                  </>
                )}
              </div>

              {/* Schedule time + Sync info */}
              <div style={{ ...card, background:'linear-gradient(135deg,#F0FDF4,#ECFDF5)', borderColor:'#86EFAC' }}>
                <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.85rem', color:'#1E293B', marginBottom:12 }}>⏰ Schedule Settings</div>

                {/* Time picker */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                  <div>
                    <label style={{ ...lbl, color:'#16A34A' }}>Post Date</label>
                    <input type="date" value={selectedDate}
                      onChange={e=>{ if(e.target.value) { setSelectedDate(e.target.value); openDate(e.target.value); } }}
                      style={{ ...inp, background:'#fff' }}/>
                  </div>
                  <div>
                    <label style={{ ...lbl, color:'#16A34A' }}>Post Time (IST)</label>
                    <input type="time" value={scheduleTime} onChange={e=>setScheduleTime(e.target.value)}
                      style={{ ...inp, background:'#fff' }}/>
                  </div>
                </div>

                {/* WhatsApp number indicator */}
                <div style={{ background:'rgba(255,255,255,0.7)', borderRadius:9, padding:'9px 12px', marginBottom:12, display:'flex', alignItems:'center', gap:8, fontSize:'0.72rem', fontFamily:F, color:'#16A34A', fontWeight:600 }}>
                  <span>💬</span>
                  <span>WhatsApp: <strong>+{whatsappNo}</strong> <span style={{ color:'#64748B', fontWeight:400 }}>(from Contact settings)</span></span>
                </div>

                {/* Sync info */}
                <div style={{ background:'rgba(255,255,255,0.7)', borderRadius:9, padding:'9px 12px', fontSize:'0.72rem', fontFamily:F, color:'#0369A1', lineHeight:1.6 }}>
                  🔄 <strong>Auto-sync:</strong> Saving also updates <strong>Daily Verse</strong> on website. Posts at <strong>{scheduleTime} IST</strong> daily via cron.
                </div>
              </div>

              {/* Save */}
              <button onClick={saveSchedule} style={{ ...btn('linear-gradient(135deg,#F59E0B,#D97706)'), width:'100%', padding:'14px', fontSize:'0.88rem', marginBottom:14, boxShadow:'0 4px 16px rgba(245,158,11,0.35)' }}>
                💾 Save to Schedule
              </button>

              {/* Manual post */}
              <div style={card}>
                <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.82rem', color:'#1E293B', marginBottom:12 }}>📤 Manual Post Now</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
                  {PLATFORMS.map(p => (
                    <button key={p.key} onClick={()=>postNow(p.key)} disabled={postStatus[p.key]==='loading'}
                      style={{ ...btn(p.bg, p.c), opacity:postStatus[p.key]==='loading'?0.65:1, fontSize:'0.72rem', padding:'10px 8px', borderRadius:10, boxShadow: p.key!=='website'?`0 3px 10px ${typeof p.bg==='string'&&p.bg.startsWith('#')?p.bg+'44':'rgba(0,0,0,0.15)'}`:undefined }}>
                      {p.label} {si(p.key)}
                    </button>
                  ))}
                </div>

                {/* Status display */}
                {Object.values(postStatus).some(v=>v&&v!=='idle') && (
                  <div style={{ background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:10, padding:'12px 14px', marginBottom:12 }}>
                    {PLATFORMS.map(p => postStatus[p.key]&&postStatus[p.key]!=='idle' && (
                      <div key={p.key} style={{ fontFamily:F, fontSize:'0.72rem', fontWeight:600, marginBottom:4, color: postStatus[p.key]==='success'?'#16A34A':postStatus[p.key]==='error'?'#DC2626':'#D97706' }}>
                        {si(p.key)} {p.label}: {postStatus[p.key]==='success'?'Sent!':postStatus[p.key]==='error'?'Failed':'Posting…'}
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={postAll} disabled={posting} style={{ ...btn('linear-gradient(135deg,#1E293B,#0F172A)','#A3E635'), width:'100%', padding:'14px', fontSize:'0.86rem', letterSpacing:'0.08em', textTransform:'uppercase', opacity:posting?0.7:1, boxShadow:'0 6px 20px rgba(30,41,59,0.35)' }}>
                  {posting ? '⏳ Posting to all…' : '🚀 Post to All Platforms'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Image Upload Slot sub-component ── */
function ImageSlot({ lang, label, preview, fileRef, uploading, aiLoading, onFile, onAI, required }) {
  const F = "'Poppins','Nunito',sans-serif";
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
        <span style={{ fontFamily:F, fontSize:'0.68rem', fontWeight:700, color:'#1E293B' }}>{label}</span>
        {!required && <span style={{ fontFamily:F, fontSize:'0.6rem', color:'#94A3B8' }}>(optional)</span>}
      </div>
      <div onClick={()=>fileRef.current.click()} style={{
        border:`2px dashed ${preview?'#84CC16':'#E2E8F0'}`,
        borderRadius:12, minHeight:118, cursor:'pointer',
        background: preview?'#F0FDF4':'#F8FAFC',
        display:'flex', alignItems:'center', justifyContent:'center',
        overflow:'hidden', transition:'all 0.15s', position:'relative',
      }}>
        {preview
          ? <img src={preview} style={{ width:'100%', height:'100%', objectFit:'cover', position:'absolute', inset:0 }}/>
          : <div style={{ textAlign:'center', color:'#94A3B8', fontFamily:F, fontSize:'0.76rem' }}>
              <div style={{ fontSize:'1.8rem', marginBottom:4 }}>🖼️</div>
              <div style={{ fontWeight:600 }}>Click to Upload</div>
              <div style={{ fontSize:'0.64rem', marginTop:2 }}>PNG, JPG, WEBP</div>
            </div>
        }
        {uploading && <div style={{ position:'absolute', inset:0, background:'rgba(255,255,255,0.8)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:F, fontWeight:700, color:'#16A34A', fontSize:'0.76rem' }}>⏳ Uploading…</div>}
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={onFile}/>
      {preview && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginTop:8 }}>
          <button onClick={()=>fileRef.current.click()} style={{ background:'#F8FAFC', border:'1.5px solid #E2E8F0', color:'#64748B', fontFamily:F, fontWeight:700, fontSize:'0.64rem', padding:'6px', borderRadius:8, cursor:'pointer' }}>🔄 Change</button>
          <button onClick={onAI} disabled={aiLoading} style={{ background:aiLoading?'#94A3B8':'#1E293B', border:'none', color:'#A3E635', fontFamily:F, fontWeight:700, fontSize:'0.64rem', padding:'6px', borderRadius:8, cursor:aiLoading?'not-allowed':'pointer', opacity:aiLoading?0.7:1 }}>
            {aiLoading?'⏳ Reading…':'🤖 AI Read'}
          </button>
        </div>
      )}
    </div>
  );
}
